import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { useWorkflowStep } from '@/context';
import type { IWorkflowStep } from '@/workflows/types';

export interface StepSidebarProps {
    steps: readonly IWorkflowStep[];
}

/**
 * Generic workflow step sidebar.
 *
 * Renders a tab for each workflow step. The active tab drives the
 * `WorkflowStepContext`, which in turn controls the main view area.
 * All step panels are force-mounted and hidden via CSS to preserve state.
 */
export function StepSidebar({ steps }: StepSidebarProps) {
    const { activeStepId, setActiveStepId } = useWorkflowStep();

    return (
        <Tabs
            value={activeStepId}
            onValueChange={setActiveStepId}
            className="flex h-full flex-col overflow-hidden"
        >
            <TabsList
                className="mx-2 mt-2 grid w-[calc(100%-1rem)]"
                style={{ gridTemplateColumns: `repeat(${steps.length}, 1fr)` }}
            >
                {steps.map((step) => (
                    <TabsTrigger key={step.id} value={step.id} className="text-xs">
                        {step.title}
                    </TabsTrigger>
                ))}
            </TabsList>

            {steps.map((step) => {
                const SidebarComponent = step.sidebar;
                return (
                    <TabsContent
                        key={step.id}
                        value={step.id}
                        forceMount
                        className="mt-0 flex-1 overflow-hidden data-[state=inactive]:hidden"
                    >
                        <SidebarComponent />
                    </TabsContent>
                );
            })}
        </Tabs>
    );
}
